import {
  Box,
  Grid,
  InputAdornment,
  List,
  ListItem,
  Typography
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { StyledButton } from "../../pages/gift-card/GiftCard";
import { extraordinaryColors } from "../../utils/theme";
import { StyledTextField } from "../ContactForm";
import { FbtVsPayeTable } from "./FbtVsPayeTable";

export interface CalculationDataInterface {
  taxRate: number;
  fbtTaxRate: number;
  kiwisaver: number;
  companyDeduction: number;
}

const calculationData: CalculationDataInterface[] = [
  {
    taxRate: 0.105,
    fbtTaxRate: 0.1173,
    kiwisaver: 0.03,
    companyDeduction: -0.28
  },
  {
    taxRate: 0.175,
    fbtTaxRate: 0.2121,
    kiwisaver: 0.03,
    companyDeduction: -0.28
  },
  {
    taxRate: 0.3,
    fbtTaxRate: 0.4286,
    kiwisaver: 0.03,
    companyDeduction: -0.28
  },
  {
    taxRate: 0.33,
    fbtTaxRate: 0.4925,
    kiwisaver: 0.03,
    companyDeduction: -0.28
  },
  {
    taxRate: 0.39,
    fbtTaxRate: 0.6393,
    kiwisaver: 0.03,
    companyDeduction: -0.28
  }
];

export interface FbtVsPayeResults {
  amount: number;
  fbtTaxRate: number;
  fbtTax: number;
  fbtKiwisaver: null;
  fbtTotalTax: number;
  fbtGrossBenefit: number;
  fbtCompanyDeduction: number;
  fbtOverallCost: number;
  taxRate: number;
  payeTax: number;
  payeKiwisaver: number;
  payeTotalTax: number;
  payeGrossBenefit: number;
  payeCompanyDeduction: number;
  payeOverallCost: number;
}

export const FbtVsPayeCalculator: FC<{
  amount: number | undefined;
  updateAmount: (value: number) => void;
}> = ({ amount, updateAmount }) => {
  const [results, setResults] = useState<FbtVsPayeResults[]>();

  const handleResults = () => {
    if (!amount) return;

    let tempResults = calculationData.map((cd) => {
      const fbtTax = cd.fbtTaxRate * amount;
      const payeTax = amount / (1 - cd.taxRate) - amount;

      const fbtKiwisaver = null;
      const payeKiwisaver = (amount + payeTax) * cd.kiwisaver;

      const fbtTotalTax = fbtTax;
      const payeTotalTax = payeTax + payeKiwisaver;

      const fbtGrossBenefit = amount + fbtTotalTax;
      const payeGrossBenefit = amount + payeTotalTax;

      const fbtCompanyDeduction = cd.companyDeduction * fbtGrossBenefit;
      const payeCompanyDeduction = cd.companyDeduction * payeGrossBenefit;

      const fbtOverallCost = fbtGrossBenefit + fbtCompanyDeduction;
      const payeOverallCost = payeGrossBenefit + payeCompanyDeduction;

      return {
        amount,
        fbtTaxRate: cd.fbtTaxRate,
        fbtTax,
        fbtKiwisaver,
        fbtTotalTax,
        fbtGrossBenefit,
        fbtCompanyDeduction: -fbtCompanyDeduction,
        fbtOverallCost,
        taxRate: cd.taxRate,
        payeTax,
        payeKiwisaver,
        payeTotalTax,
        payeGrossBenefit,
        payeCompanyDeduction: -payeCompanyDeduction,
        payeOverallCost
      };
    });

    setResults(tempResults);
  };

  useEffect(() => {
    handleResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container mt={3}>
      <Grid item xs={12} md={6}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            color: "white !important"
          }}
        >
          Update annual benefit amount{" "}
        </Typography>
        <Typography
          mb={1}
          sx={{
            color: "white !important",
            mb: 1
          }}
        >
          (For example, a $200 or $500 annual contribution)
        </Typography>

        <StyledTextField
          textcolor={extraordinaryColors.orange.main}
          variant="standard"
          value={amount}
          type="number"
          onChange={(e) => updateAmount(+e.target.value)}
          name="netBenefit"
          placeholder="Net Benefit"
          size="small"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={{
                    color: extraordinaryColors.orange.main,
                    fontSize: 25,
                    fontWeight: 700,
                    fontFamily: "GT-Alpina-Condensed",
                    paddingBottom: 0.5
                  }}
                >
                  $
                </Typography>
              </InputAdornment>
            )
          }}
          sx={{ mr: 1 }}
        />

        <StyledButton
          sx={{ mt: { xs: 2, sm: 0, md: 0, lg: 0, xl: 0 } }}
          backgroundcolor={extraordinaryColors.green.light}
          onClick={() => handleResults()}
        >
          Re-calculate
        </StyledButton>
      </Grid>

      {results && (
        <Grid container>
          <FbtVsPayeTable calculationData={calculationData} results={results} />

          <Box sx={{ p: 3, mt: 3 }}>
            <Typography
              fontWeight={700}
              gutterBottom
              sx={{ color: "white !important", fontSize: 12 }}
            >
              DISCLAIMER
            </Typography>
            <List
              sx={{
                listStyleType: "disc",
                pl: 2,
                color: "white !important",
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {[
                "The following example is based on a ‘Voucher’ (with the selected contribution amount) being provided to an employee compared to a cash payment.",
                "We have kept the example simple, however, consideration should be given as to whether the PAYE calculation should be grossed up according to the individual profile of the employee.",
                "This example compares the provision of a ‘Voucher’ with a specific contribution amount to an employee against a cash payment scenario. In the cash payment example, the employee is expecting the full contribution amount in hand, so a gross-up formula is applied to account for the PAYE cost. Consideration should be given to whether the PAYE gross-up should reflect the employee’s specific tax profile (e.g., including ACC earner levy, KiwiSaver contributions, student loan, and child support deductions) to ensure the employee receives the intended amount in hand. These factors will significantly increase the overall cost of the cash allowance approach.",
                "Also note that in this example we have considered that GST does not apply to the voucher when purchased by the employer.",
                "This calculator does not constitute tax advice. We highly recommend you seek independent advice."
              ].map((text, index) => (
                <ListItem key={index} sx={{ display: "list-item", pl: 2 }}>
                  {text}
                </ListItem>
              ))}
            </List>
            <Typography
              mt={2}
              sx={{ color: "white !important", fontSize: 12 }}
              fontWeight={700}
            >
              Prepared 7 August 2024
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
